<template>
  <div class="tencent-ai-data-page">
    <div class="page-head">
      <div>
        <h2>腾讯广告 AI 数据</h2>
        <span>查看采集层小时消耗、小时回收、日ROI、营销单元和采集日志</span>
      </div>
      <div class="head-actions">
        <el-tag size="mini" :type="statusReady ? 'success' : 'warning'">
          {{ statusReady ? '采集表可用' : '采集表未就绪' }}
        </el-tag>
        <el-button size="mini" icon="el-icon-refresh" :loading="statusLoading" @click="loadStatus">刷新状态</el-button>
      </div>
    </div>

    <section class="query-panel">
      <el-tabs v-model="activeTab" type="card" @tab-click="handleTabChange">
        <el-tab-pane v-for="tab in tabs" :key="tab.key" :label="tab.label" :name="tab.key"></el-tab-pane>
      </el-tabs>

      <el-form v-if="!isRoiRulesTab" :inline="true" :model="query" size="mini" class="query-form">
        <el-form-item label="日期">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            style="width: 250px"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="账号ID">
          <el-input v-model.trim="query.accountId" clearable placeholder="请输入账号ID" style="width: 180px"></el-input>
        </el-form-item>
        <el-form-item v-if="isAdgroupFilterVisible" label="营销单元ID">
          <el-input v-model.trim="query.adgroupId" clearable placeholder="请输入营销单元ID" style="width: 180px"></el-input>
        </el-form-item>
        <el-form-item v-if="isCreativeFilterVisible" label="创意ID">
          <el-input v-model.trim="query.creativeId" clearable placeholder="请输入创意ID" style="width: 190px"></el-input>
        </el-form-item>
        <el-form-item v-if="isOfficialLogsTab" label="对象类型">
          <el-select v-model="query.operationObjectType" placeholder="对象类型" style="width: 160px">
            <el-option label="营销单元" value="OPERATION_OBJECT_TYPE_ADGROUP"></el-option>
            <el-option label="动态创意" value="OPERATION_OBJECT_TYPE_DYNAMIC_CREATIVE"></el-option>
            <el-option label="联合预算" value="OPERATION_OBJECT_TYPE_JOINT_BUDGET"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item v-if="isOfficialLogsTab" label="操作动作">
          <el-select v-model="query.operationAction" clearable placeholder="全部" style="width: 130px">
            <el-option label="创建" value="OPERATION_ACTION_TYPE_CREATE"></el-option>
            <el-option label="更新" value="OPERATION_ACTION_TYPE_UPDATE"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item v-if="isLogsTab" label="状态">
          <el-input v-model.trim="query.status" clearable placeholder="成功 / 失败 / 异常" style="width: 150px"></el-input>
        </el-form-item>
        <el-form-item v-if="isCostFilterVisible">
          <el-checkbox v-model="query.excludeZeroCost">过滤消耗为0</el-checkbox>
        </el-form-item>
        <el-form-item label="关键词">
          <el-input v-model.trim="query.keyword" clearable placeholder="名称、ID、错误信息" style="width: 210px"></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="search">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetQuery">重置</el-button>
          <el-button icon="el-icon-download" :disabled="tableData.length === 0" @click="exportCurrentPage">导出当前页</el-button>
        </el-form-item>
      </el-form>
      <div v-else class="roi-rule-query">
        <el-input
          v-model.trim="query.keyword"
          clearable
          size="mini"
          placeholder="搜索标签或备注"
          style="width: 240px"
          @keyup.enter.native="search"
        ></el-input>
        <el-button type="primary" size="mini" icon="el-icon-search" :loading="roiRuleLoading" @click="search">查询</el-button>
        <el-button size="mini" icon="el-icon-refresh-left" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="mini" icon="el-icon-plus" @click="openRoiRuleDialog()">新增规则</el-button>
        <el-button size="mini" icon="el-icon-refresh" :loading="roiRuleLoading || roiRuleLogLoading" @click="refreshRoiRules">刷新</el-button>
      </div>
    </section>

    <section v-if="!isRoiRulesTab" class="summary-grid">
      <div v-for="item in summaryCards" :key="item.key" class="summary-card">
        <span>{{ item.label }}</span>
        <strong :class="item.className">{{ item.value }}</strong>
      </div>
    </section>

    <section v-if="!isRoiRulesTab" class="table-panel">
      <div class="table-toolbar">
        <span>{{ sourceText }}</span>
        <el-popover placement="bottom" trigger="click" width="260">
          <div class="column-options">
            <el-checkbox-group v-model="visibleColumns">
              <el-checkbox v-for="column in availableColumns" :key="column.name" :label="column.name">
                {{ displayColumnLabel(column) }}
              </el-checkbox>
            </el-checkbox-group>
          </div>
          <el-button slot="reference" size="mini" icon="el-icon-setting">列设置</el-button>
        </el-popover>
      </div>

      <el-table
        :data="tableData"
        size="mini"
        border
        stripe
        :height="tableHeight"
        v-loading="loading"
        :header-cell-style="{background:'#f8fafc', color:'#303133'}"
        @sort-change="handleSortChange"
      >
        <el-table-column
          v-for="column in renderedColumns"
          :key="column.name"
          :prop="column.name"
          :label="displayColumnLabel(column)"
          :min-width="columnWidth(column.name)"
          :sortable="isCostSortColumn(column.name) ? 'custom' : false"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <span :class="valueClass(column.name, row[column.name])">{{ formatCell(row[column.name], column.name) }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-row">
        <span>共 {{ pagination.total }} 条</span>
        <el-pagination
          background
          layout="sizes, prev, pager, next, jumper"
          :current-page="pagination.page"
          :page-size="pagination.pageSize"
          :page-sizes="[30, 50, 100, 200]"
          :total="pagination.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        ></el-pagination>
      </div>
    </section>

    <section v-if="isRoiRulesTab" class="roi-rule-panel">
      <div class="roi-auth-panel">
        <div class="roi-auth-main">
          <span class="roi-auth-title">腾讯实名认证</span>
          <el-tag size="mini" :type="userTokenTagType()">{{ userTokenStatusText() }}</el-tag>
          <span>{{ userTokenStatus.message || '用于动态创意暂停/开启接口的 user_token' }}</span>
        </div>
        <div class="roi-auth-actions">
          <span v-if="userTokenStatus.expiresAt">
            过期时间：{{ formatCell(userTokenStatus.expiresAt, 'expires_at') }}，剩余 {{ formatNumber(userTokenStatus.remainingDays || 0) }} 天
          </span>
          <el-button size="mini" icon="el-icon-refresh" :loading="userTokenLoading" @click="loadUserTokenStatus">刷新认证</el-button>
          <el-button type="primary" size="mini" icon="el-icon-key" :disabled="!userTokenStatus.authUrl" @click="openUserTokenAuth">
            {{ userTokenStatus.hasToken && !userTokenStatus.expired ? '重新认证' : '去认证' }}
          </el-button>
        </div>
      </div>
      <div class="table-toolbar">
        <span>规则列表</span>
        <span>标签前缀匹配账号标签，定时任务每 30 分钟判断当天创意 ROI</span>
        <el-button size="mini" icon="el-icon-edit" :disabled="!selectedRoiRules.length" @click="openRoiRuleBatchDialog">批量修改</el-button>
      </div>
      <el-table
        :data="roiRules"
        size="mini"
        border
        stripe
        v-loading="roiRuleLoading"
        :header-cell-style="{background:'#f8fafc', color:'#303133'}"
        @selection-change="handleRoiRuleSelectionChange"
      >
        <el-table-column type="selection" width="44"></el-table-column>
        <el-table-column prop="business_alias" label="标签" min-width="220" show-overflow-tooltip></el-table-column>
        <el-table-column prop="pause_roi_threshold" label="低于暂停" width="110">
          <template slot-scope="{ row }">{{ formatDecimal(row.pause_roi_threshold || row.roi_threshold) }}</template>
        </el-table-column>
        <el-table-column prop="enable_roi_threshold" label="高于开启" width="110">
          <template slot-scope="{ row }">{{ formatDecimal(row.enable_roi_threshold || row.roi_threshold) }}</template>
        </el-table-column>
        <el-table-column prop="min_cost_yuan" label="最低消耗" width="110">
          <template slot-scope="{ row }">{{ formatNumber(row.min_cost_yuan) }}</template>
        </el-table-column>
        <el-table-column prop="wait_minutes" label="开启后等待" width="110">
          <template slot-scope="{ row }">{{ formatNumber(row.wait_minutes) }} 分钟</template>
        </el-table-column>
        <el-table-column prop="enabled" label="状态" width="160">
          <template slot-scope="{ row }">
            <div class="status-switch-cell">
              <el-switch
                :value="Number(row.enabled) === 1"
                active-text="启用"
                inactive-text="停用"
                @change="value => toggleRoiRule(row, value)"
              ></el-switch>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="last_run_at" label="最近执行时间" width="170">
          <template slot-scope="{ row }">{{ formatCell(row.last_run_at, 'last_run_at') }}</template>
        </el-table-column>
        <el-table-column prop="matched_accounts" label="命中账户数" width="110">
          <template slot-scope="{ row }">{{ formatNumber(row.matched_accounts || 0) }}</template>
        </el-table-column>
        <el-table-column prop="matched_creatives" label="命中创意数" width="110">
          <template slot-scope="{ row }">{{ formatNumber(row.matched_creatives || 0) }}</template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="openRoiRuleDialog(row)">编辑</el-button>
            <el-button type="text" size="mini" class="danger-link" @click="deleteRoiRule(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-row">
        <span>共 {{ roiRulePagination.total }} 条规则</span>
        <el-pagination
          background
          layout="sizes, prev, pager, next, jumper"
          :current-page="roiRulePagination.page"
          :page-size="roiRulePagination.pageSize"
          :page-sizes="[20, 50, 100]"
          :total="roiRulePagination.total"
          @size-change="handleRoiRuleSizeChange"
          @current-change="handleRoiRuleCurrentChange"
        ></el-pagination>
      </div>

      <div class="table-toolbar roi-log-toolbar">
        <span>执行日志</span>
        <div class="toolbar-actions">
          <el-date-picker
            v-model="roiRuleLogDateRange"
            type="daterange"
            size="mini"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            clearable
            @change="handleRoiRuleLogDateChange"
          ></el-date-picker>
          <el-button size="mini" icon="el-icon-download" :loading="roiRuleLogExporting" @click="exportRoiRuleLogs">导出Excel</el-button>
          <el-button size="mini" icon="el-icon-refresh" :loading="roiRuleLogLoading" @click="loadRoiRuleLogs">刷新日志</el-button>
        </div>
      </div>
      <el-table
        :data="roiRuleLogs"
        size="mini"
        border
        stripe
        :height="360"
        v-loading="roiRuleLogLoading"
        :header-cell-style="{background:'#f8fafc', color:'#303133'}"
      >
        <el-table-column prop="created_at" label="执行时间" width="170">
          <template slot-scope="{ row }">{{ formatCell(row.created_at, 'created_at') }}</template>
        </el-table-column>
        <el-table-column prop="business_alias" label="标签" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="account_id" label="账号ID" width="120"></el-table-column>
        <el-table-column prop="adgroup_id" label="营销单元ID" width="130"></el-table-column>
        <el-table-column prop="dynamic_creative_id" label="创意ID" width="130"></el-table-column>
        <el-table-column prop="dynamic_creative_name" label="创意名称" min-width="220" show-overflow-tooltip></el-table-column>
        <el-table-column prop="cost_yuan" label="消耗" width="110">
          <template slot-scope="{ row }">{{ formatNumber(row.cost_yuan) }}</template>
        </el-table-column>
        <el-table-column prop="revenue_yuan" label="收入" width="110">
          <template slot-scope="{ row }">{{ formatNumber(row.revenue_yuan) }}</template>
        </el-table-column>
        <el-table-column prop="roi_value" label="ROI" width="100">
          <template slot-scope="{ row }">{{ formatDecimal(row.roi_value) }}</template>
        </el-table-column>
        <el-table-column prop="threshold_value" label="阈值" width="100">
          <template slot-scope="{ row }">{{ formatDecimal(row.threshold_value) }}</template>
        </el-table-column>
        <el-table-column prop="action" label="动作" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="roiRuleActionType(row.action)">{{ roiRuleActionText(row.action) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="success" label="结果" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="Number(row.success) === 1 ? 'success' : 'danger'">
              {{ Number(row.success) === 1 ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="reason" label="原因" min-width="220" show-overflow-tooltip></el-table-column>
      </el-table>
      <div class="pagination-row">
        <span>共 {{ roiRuleLogPagination.total }} 条日志</span>
        <el-pagination
          background
          layout="sizes, prev, pager, next, jumper"
          :current-page="roiRuleLogPagination.page"
          :page-size="roiRuleLogPagination.pageSize"
          :page-sizes="[30, 50, 100, 200]"
          :total="roiRuleLogPagination.total"
          @size-change="handleRoiRuleLogSizeChange"
          @current-change="handleRoiRuleLogCurrentChange"
        ></el-pagination>
      </div>
    </section>

    <section v-if="!isRoiRulesTab" class="status-panel">
      <div class="status-title">采集表状态</div>
      <el-table
        :data="relations"
        size="mini"
        border
        :height="260"
        :header-cell-style="{background:'#f8fafc', color:'#303133'}"
      >
        <el-table-column prop="name" label="表 / 视图" min-width="260" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ displaySourceName(row.name) }}</template>
        </el-table-column>
        <el-table-column prop="exists" label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.exists ? 'success' : 'danger'">{{ row.exists ? '存在' : '缺失' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="rows" label="估算行数" width="120">
          <template slot-scope="{ row }">{{ formatNumber(row.rows || 0) }}</template>
        </el-table-column>
        <el-table-column prop="columns" label="列数" width="90"></el-table-column>
        <el-table-column prop="latest" label="最新时间" min-width="180" show-overflow-tooltip></el-table-column>
      </el-table>
    </section>

    <el-dialog
      :title="roiRuleEditing ? '编辑 ROI 自动规则' : '新增 ROI 自动规则'"
      :visible.sync="roiRuleDialogVisible"
      width="520px"
      append-to-body
    >
      <el-form :model="roiRuleForm" label-width="130px" size="small">
        <el-form-item label="标签">
          <el-select
            v-model="roiRuleForm.businessAlias"
            filterable
            remote
            allow-create
            default-first-option
            :remote-method="searchRoiRuleLabels"
            :loading="roiRuleLabelLoading"
            placeholder="输入标签前缀或搜索已有标签"
            style="width: 100%"
          >
            <el-option v-for="label in roiRuleLabels" :key="label" :label="label" :value="label"></el-option>
          </el-select>
          <div class="form-hint">保存一条标签前缀规则；例如填写“标签A”，会匹配“标签A”后面带任意字符的账号标签。</div>
        </el-form-item>
        <el-form-item label="低于此 ROI 暂停">
          <el-input-number v-model="roiRuleForm.pauseRoiThreshold" :min="0" :step="0.01" :precision="4" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="高于此 ROI 开启">
          <el-input-number v-model="roiRuleForm.enableRoiThreshold" :min="0" :step="0.01" :precision="4" style="width: 100%"></el-input-number>
          <div class="form-hint">开启阈值应大于或等于暂停阈值，中间区间不会自动开关。</div>
        </el-form-item>
        <el-form-item label="最低消耗">
          <el-input-number v-model="roiRuleForm.minCostYuan" :min="0" :step="10" :precision="2" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="开启后等待">
          <el-input-number v-model="roiRuleForm.waitMinutes" :min="0" :step="30" :precision="0" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="启用规则">
          <el-switch v-model="roiRuleForm.enabled"></el-switch>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model.trim="roiRuleForm.remark" type="textarea" :rows="3" maxlength="1000" show-word-limit></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="roiRuleDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="roiRuleSaving" @click="submitRoiRule">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="批量修改 ROI 自动规则"
      :visible.sync="roiRuleBatchDialogVisible"
      width="560px"
      append-to-body
    >
      <div class="selected-count">已选择 {{ selectedRoiRules.length }} 条规则。只会修改勾选的字段。</div>
      <el-form :model="roiRuleBatchForm" label-width="150px" size="small">
        <div class="batch-field-row">
          <el-checkbox v-model="roiRuleBatchFields.pauseRoiThreshold">修改低于暂停 ROI</el-checkbox>
          <el-input-number v-model="roiRuleBatchForm.pauseRoiThreshold" :disabled="!roiRuleBatchFields.pauseRoiThreshold" :min="0" :step="0.01" :precision="4"></el-input-number>
        </div>
        <div class="batch-field-row">
          <el-checkbox v-model="roiRuleBatchFields.enableRoiThreshold">修改高于开启 ROI</el-checkbox>
          <el-input-number v-model="roiRuleBatchForm.enableRoiThreshold" :disabled="!roiRuleBatchFields.enableRoiThreshold" :min="0" :step="0.01" :precision="4"></el-input-number>
        </div>
        <div class="batch-field-row">
          <el-checkbox v-model="roiRuleBatchFields.minCostYuan">修改最低消耗</el-checkbox>
          <el-input-number v-model="roiRuleBatchForm.minCostYuan" :disabled="!roiRuleBatchFields.minCostYuan" :min="0" :step="10" :precision="2"></el-input-number>
        </div>
        <div class="batch-field-row">
          <el-checkbox v-model="roiRuleBatchFields.waitMinutes">修改开启后等待</el-checkbox>
          <el-input-number v-model="roiRuleBatchForm.waitMinutes" :disabled="!roiRuleBatchFields.waitMinutes" :min="0" :step="30" :precision="0"></el-input-number>
        </div>
        <div class="batch-field-row">
          <el-checkbox v-model="roiRuleBatchFields.enabled">修改启用状态</el-checkbox>
          <el-switch v-model="roiRuleBatchForm.enabled" :disabled="!roiRuleBatchFields.enabled" active-text="启用" inactive-text="停用"></el-switch>
        </div>
        <div class="batch-field-row align-start">
          <el-checkbox v-model="roiRuleBatchFields.remark">修改备注</el-checkbox>
          <el-input v-model.trim="roiRuleBatchForm.remark" :disabled="!roiRuleBatchFields.remark" type="textarea" :rows="3" maxlength="1000" show-word-limit></el-input>
        </div>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="roiRuleBatchDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="roiRuleBatchSaving" @click="submitRoiRuleBatchUpdate">保存修改</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
const SOURCE_LABEL_MAP = {
  ai_tencent_creative_hourly_wide_view: '小时数据宽表',
  ai_tencent_adgroup_hourly_report: '营销单元小时报表',
  ai_tencent_creative_daily_roi_wide_view: '日ROI宽表',
  ai_tencent_adgroup_hourly_snapshot: '营销单元小时快照',
  ai_tencent_active_account_day: '有消耗账号每日快照',
  ai_tencent_active_account_hour: '有消耗账号小时快照',
  ai_tencent_creative_hourly_spend: '创意小时消耗表',
  ai_tencent_creative_hourly_recovery: '创意小时回收表',
  ai_tencent_creative_daily_roi: '创意日ROI表',
  ai_tencent_creative_asset_map: '创意素材映射表',
  ai_tencent_etl_log: '采集日志表',
  ai_tencent_operation_log: '腾讯官方操作日志表',
  ai_tencent_roi_label_rule: 'ROI 自动规则表',
  ai_tencent_roi_creative_state: 'ROI 自动规则创意状态表',
  ai_tencent_roi_action_log: 'ROI 自动规则执行日志表',
  'operation_log_list/get': '腾讯官方操作日志'
}

const COLUMN_LABEL_MAP = {
  id: 'ID',
  stat_time_hour: '数据小时',
  snapshot_hour: '采集小时',
  stat_date: '数据日期',
  stat_hour: '小时',
  pull_hour: '拉取小时',
  account_id: '账号ID',
  advertiser_id: '账号ID',
  account_name: '账号名称',
  advertiser_name: '账号名称',
  business_alias: '客户工作台账号标签',
  adgroup_id: '营销单元ID',
  ad_group_id: '营销单元ID',
  campaign_id: '营销单元ID',
  adgroup_name: '营销单元名称',
  campaign_name: '营销单元名称',
  dynamic_creative_id: '动态创意ID',
  creative_id: '创意ID',
  material_id: '素材ID',
  dynamic_creative_name: '创意名称',
  creative_name: '创意名称',
  material_name: '素材名称',
  creative_template_id: '创意形式ID',
  creative_template_name: '官方创意形式',
  creative_template_style: '创意形式类型',
  creative_template_size: '创意形式尺寸',
  component_selector_type: '组件选择类型',
  component_type: '组件类型',
  component_id: '组件ID',
  asset_type: '素材类型',
  asset_id: '素材ID',
  asset_extra_json: '素材原始信息',
  material_format_code: '素材形式代码',
  material_format_name: '素材形式',
  material_format_size: '素材尺寸',
  material_component_summary: '素材组件摘要',
  game_name: '游戏名称',
  cost_yuan: '消耗',
  stat_cost_yuan: '消耗',
  backend_cost_yuan: '后台消耗',
  today_cost_yuan: '当日消耗',
  one_click_cost_yuan: '一键起量消耗',
  real_cost_value_explore_yuan: '跑量优先消耗',
  roi_threshold: '兼容 ROI 阈值',
  pause_roi_threshold: '低于此 ROI 暂停',
  enable_roi_threshold: '高于此 ROI 开启',
  min_cost_yuan: '最低消耗',
  wait_minutes: '开启后等待分钟',
  enabled: '启用',
  remark: '备注',
  last_run_at: '最近执行时间',
  matched_accounts: '命中账户数',
  matched_creatives: '命中创意数',
  threshold_value: '规则阈值',
  revenue_yuan: '收入',
  old_status: '原状态',
  new_status: '新状态',
  action: '动作',
  reason: '原因',
  success: '是否成功',
  view_count: '展现数',
  valid_click_count: '有效点击数',
  ctr: '点击率',
  cpc_yuan: '点击成本',
  cpm_yuan: '千次展现均价',
  conversions_count: '转化数',
  conversions_cost_yuan: '转化成本',
  conversions_rate: '转化率',
  mini_game_register_cost_yuan: '注册人数成本（平台上报）',
  mini_game_register_users: '注册人数（平台上报）',
  mini_game_register_rate: '注册率（平台上报）',
  mini_game_retention_d1_rate: '次日留存率（平台上报）',
  daily_budget_yuan: '日预算',
  total_budget_yuan: '总预算',
  bid_amount_yuan: '出价',
  bid_mode: '出价方式',
  smart_delivery_platform: '智投类型',
  smart_delivery_scene: '智投场景',
  smart_delivery_scene_spec_json: '智投场景原始配置',
  smart_bid_type: '智能出价类型',
  bid_scene: '出价场景',
  bid_adjustment_json: '出价调整原始配置',
  bid_adjustment_text: '出价调整',
  bid_coefficient: '浅层提价系数',
  deep_bid_coefficient: '深层提价系数',
  auto_acquisition_enabled: '一键起量开启',
  auto_acquisition_budget_yuan: '一键起量金额',
  auto_acquisition_status: '一键起量状态',
  auto_acquisition_status_text: '一键起量状态说明',
  expected_roi: 'ROI期望',
  expected_roi_goal: 'ROI目标',
  deep_conversion_type: '深度转化类型',
  deep_conversion_worth_rate: '深度优化价值出价',
  deep_conversion_worth_advanced_rate: '深度优化高级价值出价',
  deep_conversion_behavior_bid_yuan: '深度转化行为出价',
  deep_conversion_behavior_advanced_bid_yuan: '深度转化高级行为出价',
  marketing_target_type: '营销目标类型',
  marketing_goal: '营销目标',
  marketing_carrier_type: '营销载体类型',
  marketing_carrier_id: '营销载体ID',
  optimization_goal: '优化目标',
  configured_status: '配置状态',
  system_status: '系统状态',
  begin_date: '开始日期',
  end_date: '结束日期',
  time_series: '投放时段',
  targeting_translation: '定向描述',
  targeting_age_text: '年龄定向',
  targeting_gender_text: '性别定向',
  targeting_geo_text: '地域定向',
  site_set_text: '投放版位',
  priority_site_set_text: '优选版位',
  priority_site_set_json: '优选版位原始配置',
  automatic_site_enabled: '自动版位',
  scene_spec_text: '场景定向',
  first_day_begin_time: '首日开始时间',
  flow_optimization_enabled: '是否使用自动流量优选',
  search_expansion_switch: '搜索扩量开关',
  ecom_pkam_switch: '一方人群跑量加强开关状态',
  aoi_optimization_strategy_json: '高价值范围探索原始配置',
  aoi_optimization_strategy_enabled: '高价值范围探索',
  aoi_id_list_json: '高价值范围探索人群ID原始配置',
  aoi_id_list_text: '高价值范围探索人群ID',
  cost_guarantee_status: '成本保障状态',
  cost_guarantee_money_yuan: '成本保障赔付金额',
  targeting_json: '定向原始配置',
  site_set_json: '版位原始配置',
  scene_spec_json: '场景原始配置',
  rank_no: '消耗排名',
  raw_json: '原始数据',
  extra_json: '附加信息',
  created_at: '创建时间',
  updated_at: '更新时间',
  job_name: '采集任务',
  status: '状态',
  message: '信息',
  created_time_text: '操作时间',
  operation_action_text: '操作动作',
  fronted_operator: '操作人',
  operator_platform_text: '操作平台',
  operation_object_type: '操作对象类型',
  operation_object_id: '操作对象ID',
  operation_object_name: '操作对象名称',
  operation_log: '官方操作内容',
  operation_log_text: '官方操作内容',
  operation_log_json: '官方操作内容原始数据',
  operation_info_list_json: '批量操作明细原始数据',
  operation_info_text: '批量操作明细',
  operation_action: '操作动作原值',
  operator_platform: '操作平台原值',
  created_time_at: '操作时间',
  fetched_at: '采集时间',
  rows: '行数',
  columns: '列数',
  latest: '最新时间',
  source: '来源',
  pull_date: '拉取日期',
  day_num: 'ROI天数',
  roi_value: 'ROI值',
  roi_field_name: 'ROI字段',
  revenue_field_name: '收入字段',
  field_present: '字段是否返回',
  revenue_present: '收入字段是否返回',
  income_val_24h_yuan: '24小时收入',
  current_revenue_hour: '当前回查H',
  current_revenue_yuan: '当前回查累计回收',
  mini_game_income_roi_1: '小游戏首日ROI',
  mini_game_income_roi_24h: '注册首24小时广告变现ROI（平台上报）',
  mini_game_income_roi_3: '注册3日广告变现ROI（平台上报）',
  mini_game_income_roi_7: '注册7日广告变现ROI（平台上报）',
  mini_game_ad_monetization_roi: '小游戏广告变现ROI',
  mini_game_ad_monetization_arpu_yuan: '广告变现ARPU（平台上报）',
  mini_game_first_day_ad_paying_arpu_yuan: '注册首日广告付费ARPU（平台上报）',
  mini_game_first_day_ad_monetization_arpu_yuan: '注册首日广告变现ARPU（平台上报）',
  mini_game_ad_monetization_amount_yuan: '广告变现金额（平台上报）',
  mini_game_first_day_ad_monetization_amount_yuan: '注册首日广告变现金额（平台上报）',
  cumulative_roi: '累计ROI',
  cumulative_revenue_yuan: '累计回收',
  incremental_revenue_yuan: '新增回收'
}

const VALUE_LABEL_MAP = {
  AD_STATUS_NORMAL: '正常',
  AD_STATUS_SUSPEND: '暂停',
  ADGROUP_STATUS_ACTIVE: '投放中',
  ADGROUP_STATUS_PARTIAL_ACTIVE: '部分投放中',
  ADGROUP_STATUS_ACCOUNT_BALANCE_NOT_ENOUGH: '账号余额不足',
  ADGROUP_STATUS_SUSPEND: '已暂停',
  ADGROUP_STATUS_CREATIVE_STATUS_PENDING: '创意审核中',
  ADGROUP_STATUS_NOT_IN_DELIVERY_TIME: '非投放时段',
  SMART_ADGROUP_STATUS_USING: '智能广告投放中',
  SMART_ADGROUP_STATUS_SUSPEND: '智能广告暂停',
  SMART_ADGROUP_STATUS_ACCOUNT_BALANCE_NOT_ENOUGH: '智能广告账号余额不足',
  SMART_ADGROUP_STATUS_NOT_IN_DELIVERY_TIME: '智能广告非投放时段',
  SMART_DELIVERY_PLATFORM_EDITION_MINI_GAME_PROMOTION: '小游戏推广智投',
  SMART_DELIVERY_SCENE_STANDARD: '标准投放',
  SMART_DELIVERY_GOAL_SEVEN_DAY_MONETIZATION: '7日变现目标',
  SMART_DELIVERY_GOAL_ONE_DAY_MONETIZATION: '1日变现目标',
  SMART_DELIVERY_GOAL_THIRTY_DAY_MONETIZATION: '30日变现目标',
  BID_MODE_OCPM: 'OCPM出价',
  BID_MODE_CPC: 'CPC出价',
  BID_MODE_CPM: 'CPM出价',
  BID_SCENE_NORMAL_AVERAGE: '普通均衡出价',
  SEARCH_EXPANSION_SWITCH_OPEN: '打开',
  SEARCH_EXPANSION_SWITCH_CLOSE: '关闭',
  ECOM_PKAM_SWITCH_OPEN: '打开',
  ECOM_PKAM_SWITCH_CLOSE: '关闭',
  AUTO_ACQUISTION_STATUS_ADVERTISER_CLOSED: '广告主关闭',
  AUTO_ACQUISTION_STATUS_COMPLETED: '已完成',
  AUTO_ACQUISTION_STATUS_PENDING: '待开启',
  AUTO_ACQUISTION_STATUS_SUSPEND_ON_PLAYING_FAIL: '投放失败暂停',
  AUTO_ACQUISTION_STATUS_UNKNOW: '未知',
  AUTO_ACQUISITION_STATUS_ADVERTISER_CLOSED: '广告主关闭',
  AUTO_ACQUISITION_STATUS_COMPLETED: '已完成',
  AUTO_ACQUISITION_STATUS_PENDING: '待开启',
  AUTO_ACQUISITION_STATUS_SUSPEND_ON_PLAYING_FAIL: '投放失败暂停',
  AUTO_ACQUISITION_STATUS_UNKNOWN: '未知',
  OPERATION_ACTION_TYPE_CREATE: '创建',
  OPERATION_ACTION_TYPE_UPDATE: '更新',
  OPERATOR_PLATFORM_MARKETING_API: '营销API',
  OPERATOR_PLATFORM_ADQ: '广告投放端',
  OPERATOR_PLATFORM_AUDIT: '审核平台',
  OPERATOR_PLATFORM_MINISITE: '轻站',
  OPERATOR_PLATFORM_SMART_ENGINE: '智能引擎',
  OPERATOR_PLATFORM_SEARCH_EDITOR: '搜索编辑器',
  OPERATOR_PLATFORM_MARKETING_ASSET: '营销资产',
  OPERATOR_PLATFORM_API_KEY: 'API Key',
  OPERATOR_PLATFORM_UNKNOWN: '未知平台',
  OPERATION_OBJECT_TYPE_ADGROUP: '营销单元',
  OPERATION_OBJECT_TYPE_DYNAMIC_CREATIVE: '动态创意',
  OPERATION_OBJECT_TYPE_JOINT_BUDGET: '联合预算',
  GOAL_1DAY_MONETIZATION_ROAS: '1日变现ROI目标',
  GOAL_7DAY_MONETIZATION_ROAS: '7日变现ROI目标',
  GOAL_30DAY_MONETIZATION_ROAS: '30日变现ROI目标',
  DEEP_CONVERSION_WORTH: '深度转化价值',
  MARKETING_TARGET_TYPE_WECHAT_MINI_GAME: '微信小游戏',
  MARKETING_GOAL_USER_GROWTH: '用户增长',
  MARKETING_CARRIER_TYPE_WECHAT_MINI_GAME: '微信小游戏',
  OPTIMIZATIONGOAL_APP_REGISTER: '应用注册',
  SITE_SET_WECHAT_PLUGIN: '微信小程序',
  SITE_SET_PCQQ: 'PC QQ',
  SITE_SET_MOBILE_UNION: '移动联盟',
  SITE_SET_KANDIAN: '腾讯看点',
  SITE_SET_TENCENT_NEWS: '腾讯新闻',
  SITE_SET_TENCENT_VIDEO: '腾讯视频',
  SITE_SET_MOMENTS: '朋友圈',
  SITE_SET_QQ_MUSIC_GAME: 'QQ音乐/游戏',
  SITE_SET_WECHAT: '微信',
  SITE_SET_CHANNELS: '视频号',
  SITE_SET_SEARCH_SCENE: '搜索场景',
  ACTIVE_TIME: '激活时间口径',
  OPEN_API: '开放接口',
  ok: '成功',
  success: '成功',
  failed: '失败',
  fail: '失败',
  error: '异常',
  running: '运行中'
}

const JSON_KEY_LABEL_MAP = {
  site_set_package: '版位提价',
  site_set: '版位',
  bid_coefficient: '浅层提价系数',
  deep_bid_coefficient: '深层提价系数',
  aoi_optimization_strategy_enabled: '高价值范围探索',
  aoi_id_list: '高价值范围探索人群ID',
  cost_guarantee_status: '成本保障状态',
  cost_guarantee_money: '成本保障赔付金额',
  conversion_id_list: '转化ID',
  smart_delivery_goal: '智投目标',
  smart_delivery_goal_spec: '智投目标配置',
  mini_game_promotion_spec: '小游戏推广配置',
  register_cost: '注册成本',
  seven_day_monetization_roi: '7日变现ROI',
  one_day_monetization_roi: '1日变现ROI',
  thirty_day_monetization_roi: '30日变现ROI'
}

const COLUMN_PART_LABEL_MAP = {
  account: '账号',
  advertiser: '账号',
  adgroup: '营销单元',
  ad: '广告',
  group: '组',
  campaign: '营销单元',
  dynamic: '动态',
  creative: '创意',
  material: '素材',
  template: '形式',
  format: '形式',
  component: '组件',
  selector: '选择',
  summary: '摘要',
  game: '游戏',
  stat: '数据',
  snapshot: '快照',
  pull: '拉取',
  backend: '后台',
  today: '当日',
  daily: '每日',
  total: '总',
  budget: '预算',
  bid: '出价',
  mode: '方式',
  amount: '金额',
  yuan: '元',
  view: '展现',
  valid: '有效',
  click: '点击',
  count: '数',
  conversions: '转化',
  conversion: '转化',
  cost: '消耗',
  spend: '消耗',
  roi: 'ROI',
  revenue: '收入',
  income: '收入',
  recovery: '回收',
  cumulative: '累计',
  incremental: '新增',
  increment: '新增',
  register: '注册',
  users: '人数',
  retention: '留存',
  rate: '率',
  monetization: '广告变现',
  mini: '小游戏',
  target: '目标',
  targeting: '定向',
  translation: '描述',
  age: '年龄',
  gender: '性别',
  geo: '地域',
  site: '版位',
  set: '集合',
  automatic: '自动',
  auto: '自动',
  acquisition: '起量',
  enabled: '是否开启',
  smart: '智能',
  delivery: '投放',
  platform: '平台',
  worth: '价值',
  advanced: '高级',
  behavior: '行为',
  paying: '付费',
  arpu: 'ARPU',
  one: '一键',
  first: '首日',
  scene: '场景',
  spec: '配置',
  json: '原始数据',
  raw: '原始',
  extra: '附加',
  job: '任务',
  name: '名称',
  field: '字段',
  present: '是否返回',
  source: '来源',
  status: '状态',
  system: '系统',
  configured: '配置',
  type: '类型',
  goal: '目标',
  carrier: '载体',
  marketing: '营销',
  optimization: '优化',
  begin: '开始',
  end: '结束',
  time: '时间',
  hour: '小时',
  date: '日期',
  rank: '排名',
  no: '序号',
  message: '信息',
  error: '错误',
  rows: '行数',
  row: '行',
  columns: '列数',
  column: '列',
  latest: '最新',
  appid: 'AppID',
  app: '应用'
}

export default {
  name: 'TencentAiData',
  data() {
    return {
      tabs: [
        { key: 'hourly', label: '小时数据', endpoint: '/tencent-ai/hourly' },
        { key: 'adgroupHourly', label: '营销单元小时', endpoint: '/tencent-ai/adgroup-hourly' },
        { key: 'daily', label: '日 ROI', endpoint: '/tencent-ai/daily-roi' },
        { key: 'adgroups', label: '营销单元', endpoint: '/tencent-ai/adgroups' },
        { key: 'accounts', label: '有消耗账号', endpoint: '/tencent-ai/accounts' },
        { key: 'operationLogs', label: '官方操作日志', endpoint: '/tencent-ai/operation-logs' },
        { key: 'roiRules', label: 'ROI 自动规则', endpoint: '/tencent-ai/roi-rules' },
        { key: 'logs', label: '采集日志', endpoint: '/tencent-ai/logs' }
      ],
      activeTab: 'hourly',
      dateRange: [],
      query: {
        accountId: '',
        adgroupId: '',
        creativeId: '',
        status: '',
        keyword: '',
        operationObjectType: 'OPERATION_OBJECT_TYPE_ADGROUP',
        operationAction: '',
        excludeZeroCost: false
      },
      pagination: {
        page: 1,
        pageSize: 50,
        total: 0
      },
      sort: {
        field: '',
        order: ''
      },
      loading: false,
      statusLoading: false,
      statusReady: false,
      relations: [],
      tableData: [],
      availableColumns: [],
      visibleColumns: [],
      summary: {},
      source: '',
      tableHeight: 520,
      roiRuleLoading: false,
      roiRuleLogLoading: false,
      roiRuleLogExporting: false,
      roiRuleLabelLoading: false,
      userTokenLoading: false,
      userTokenStatus: {},
      roiRuleSaving: false,
      roiRuleBatchSaving: false,
      roiRules: [],
      roiRuleLogs: [],
      roiRuleLabels: [],
      roiRuleDialogVisible: false,
      roiRuleBatchDialogVisible: false,
      roiRuleEditing: null,
      selectedRoiRules: [],
      roiRuleLogDateRange: [],
      roiRuleForm: {
        businessAlias: '',
        pauseRoiThreshold: 0.12,
        enableRoiThreshold: 0.12,
        minCostYuan: 100,
        waitMinutes: 120,
        enabled: true,
        remark: ''
      },
      roiRuleBatchFields: {
        pauseRoiThreshold: false,
        enableRoiThreshold: false,
        minCostYuan: false,
        waitMinutes: false,
        enabled: false,
        remark: false
      },
      roiRuleBatchForm: {
        pauseRoiThreshold: 0.12,
        enableRoiThreshold: 0.12,
        minCostYuan: 100,
        waitMinutes: 120,
        enabled: true,
        remark: ''
      },
      roiRulePagination: {
        page: 1,
        pageSize: 50,
        total: 0
      },
      roiRuleLogPagination: {
        page: 1,
        pageSize: 50,
        total: 0
      }
    }
  },
  computed: {
    activeConfig() {
      return this.tabs.find(item => item.key === this.activeTab) || this.tabs[0]
    },
    isAccountsTab() {
      return this.activeTab === 'accounts'
    },
    isLogsTab() {
      return this.activeTab === 'logs'
    },
    isOfficialLogsTab() {
      return this.activeTab === 'operationLogs'
    },
    isRoiRulesTab() {
      return this.activeTab === 'roiRules'
    },
    isCostFilterVisible() {
      return !this.isLogsTab && !this.isOfficialLogsTab && !this.isRoiRulesTab
    },
    isAdgroupFilterVisible() {
      if (this.isAccountsTab || this.isLogsTab) {
        return false
      }
      return !this.isOfficialLogsTab || this.query.operationObjectType === 'OPERATION_OBJECT_TYPE_ADGROUP'
    },
    isCreativeFilterVisible() {
      return this.activeTab === 'hourly' ||
        this.activeTab === 'daily' ||
        (this.isOfficialLogsTab && this.query.operationObjectType === 'OPERATION_OBJECT_TYPE_DYNAMIC_CREATIVE')
    },
    sourceText() {
      return this.source ? '数据源：' + this.displaySourceName(this.source) : '数据源：-'
    },
    renderedColumns() {
      const selected = this.visibleColumns.length > 0
        ? this.visibleColumns
        : this.availableColumns.slice(0, 12).map(item => item.name)
      return selected
        .map(name => this.availableColumns.find(item => item.name === name) || { name, label: name })
        .filter(item => item && item.name)
    },
    summaryCards() {
      return [
        { key: 'rows', label: '匹配行数', value: this.formatNumber(this.summary.rows || 0), className: '' },
        { key: 'cost', label: '总消耗', value: this.formatNumber(this.summary.totalCost), className: 'cost' },
        { key: 'recovery', label: '总回收', value: this.formatNumber(this.summary.totalRecovery), className: 'recovery' },
        { key: 'roi1', label: '平均 ROI1', value: this.formatDecimal(this.summary.avgRoi1), className: 'roi' },
        { key: 'roi7', label: '平均 ROI7', value: this.formatDecimal(this.summary.avgRoi7), className: 'roi' }
      ]
    }
  },
  mounted() {
    this.setDefaultDateRange()
    this.calculateTableHeight()
    window.addEventListener('resize', this.calculateTableHeight)
    this.loadStatus()
    this.loadData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    setDefaultDateRange() {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 6)
      this.dateRange = [this.formatDate(start), this.formatDate(end)]
    },
    handleTabChange() {
      this.pagination.page = 1
      this.tableData = []
      this.availableColumns = []
      this.visibleColumns = []
      this.source = ''
      this.summary = {}
      this.sort = { field: '', order: '' }
      if (this.isRoiRulesTab) {
        this.roiRulePagination.page = 1
        this.roiRuleLogPagination.page = 1
        this.roiRuleLogDateRange = []
        this.refreshRoiRules()
        return
      }
      this.loadData()
    },
    search() {
      if (this.isRoiRulesTab) {
        this.roiRulePagination.page = 1
        this.roiRuleLogPagination.page = 1
        this.refreshRoiRules()
        return
      }
      this.pagination.page = 1
      this.loadData()
    },
    resetQuery() {
      this.setDefaultDateRange()
      this.query = {
        accountId: '',
        adgroupId: '',
        creativeId: '',
        status: '',
        keyword: '',
        operationObjectType: 'OPERATION_OBJECT_TYPE_ADGROUP',
        operationAction: '',
        excludeZeroCost: false
      }
      if (this.isRoiRulesTab) {
        this.roiRulePagination.page = 1
        this.roiRuleLogPagination.page = 1
        this.refreshRoiRules()
        return
      }
      this.pagination.page = 1
      this.loadData()
    },
    async loadStatus() {
      this.statusLoading = true
      try {
        const response = await this.$axios.get('/tencent-ai/status')
        if (response.data.success) {
          const data = response.data.data || {}
          this.statusReady = !!data.ready
          this.relations = data.relations || []
        } else {
          this.statusReady = false
          this.$message.error(response.data.msg || '状态查询失败')
        }
      } catch (error) {
        this.statusReady = false
        this.$message.error('状态查询失败')
      } finally {
        this.statusLoading = false
      }
    },
    async loadData() {
      if (this.isRoiRulesTab) {
        await this.refreshRoiRules()
        return
      }
      this.loading = true
      try {
        const response = await this.$axios.post(this.activeConfig.endpoint, this.buildPayload())
        if (response.data.success) {
          this.applyResult(response.data.data || {})
        } else {
          this.applyResult({})
          this.$message.error(response.data.msg || '查询失败')
        }
      } catch (error) {
        this.applyResult({})
        this.$message.error('查询失败')
      } finally {
        this.loading = false
      }
    },
    async refreshRoiRules() {
      await Promise.all([
        this.loadRoiRules(),
        this.loadRoiRuleLogs(),
        this.loadRoiRuleLabels(),
        this.loadUserTokenStatus()
      ])
    },
    async loadUserTokenStatus() {
      this.userTokenLoading = true
      try {
        const response = await this.$axios.get('/tencent-ai/user-token/status')
        if (response.data.success) {
          this.userTokenStatus = response.data.data || {}
        } else {
          this.userTokenStatus = { message: response.data.msg || '认证状态查询失败' }
        }
      } catch (error) {
        this.userTokenStatus = { message: '认证状态查询失败' }
      } finally {
        this.userTokenLoading = false
      }
    },
    openUserTokenAuth() {
      const url = this.userTokenStatus && this.userTokenStatus.authUrl
      if (!url) {
        this.$message.warning('授权链接暂不可用')
        return
      }
      window.open(url, '_blank')
      this.$message.info('完成腾讯认证后，回到本页刷新认证状态')
    },
    userTokenStatusText() {
      if (!this.userTokenStatus || !this.userTokenStatus.oauthTokenReady) return '未配置'
      if (!this.userTokenStatus.hasToken) return '未认证'
      if (this.userTokenStatus.expired) return '已过期'
      return '有效'
    },
    userTokenTagType() {
      if (!this.userTokenStatus || !this.userTokenStatus.oauthTokenReady) return 'info'
      if (!this.userTokenStatus.hasToken || this.userTokenStatus.expired) return 'danger'
      const hours = Number(this.userTokenStatus.remainingHours || 0)
      return hours <= 48 ? 'warning' : 'success'
    },
    async loadRoiRules() {
      this.roiRuleLoading = true
      try {
        const response = await this.$axios.get('/tencent-ai/roi-rules', {
          params: {
            keyword: this.query.keyword,
            page: this.roiRulePagination.page,
            pageSize: this.roiRulePagination.pageSize
          }
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.roiRules = data.rows || []
          this.roiRulePagination.total = Number(data.total || 0)
          this.roiRulePagination.page = Number(data.page || this.roiRulePagination.page)
          this.roiRulePagination.pageSize = Number(data.pageSize || this.roiRulePagination.pageSize)
        } else {
          this.roiRules = []
          this.$message.error(response.data.msg || '规则查询失败')
        }
      } catch (error) {
        this.roiRules = []
        this.$message.error('规则查询失败')
      } finally {
        this.roiRuleLoading = false
      }
    },
    async loadRoiRuleLabels(keyword = '') {
      this.roiRuleLabelLoading = true
      try {
        const response = await this.$axios.get('/tencent-ai/roi-rules/labels', {
          params: { keyword, limit: 50 }
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.roiRuleLabels = data.labels || []
        }
      } catch (error) {
        this.roiRuleLabels = []
      } finally {
        this.roiRuleLabelLoading = false
      }
    },
    searchRoiRuleLabels(keyword) {
      this.loadRoiRuleLabels(keyword)
    },
    selectAllRoiRuleLabels() {
      this.roiRuleForm.businessAlias = (this.roiRuleLabels || [])[0] || ''
    },
    clearSelectedRoiRuleLabels() {
      this.roiRuleForm.businessAlias = ''
    },
    handleRoiRuleSelectionChange(rows) {
      this.selectedRoiRules = rows || []
    },
    emptyRoiRuleBatchForm() {
      return {
        pauseRoiThreshold: 0.12,
        enableRoiThreshold: 0.12,
        minCostYuan: 100,
        waitMinutes: 120,
        enabled: true,
        remark: ''
      }
    },
    openRoiRuleBatchDialog() {
      if (!this.selectedRoiRules.length) {
        this.$message.warning('请先选择规则')
        return
      }
      this.roiRuleBatchFields = {
        pauseRoiThreshold: false,
        enableRoiThreshold: false,
        minCostYuan: false,
        waitMinutes: false,
        enabled: false,
        remark: false
      }
      this.roiRuleBatchForm = this.emptyRoiRuleBatchForm()
      this.roiRuleBatchDialogVisible = true
    },
    buildRoiRuleBatchPayload() {
      const payload = {
        ids: this.selectedRoiRules.map(row => row.id).filter(Boolean)
      }
      if (this.roiRuleBatchFields.pauseRoiThreshold) payload.pauseRoiThreshold = this.roiRuleBatchForm.pauseRoiThreshold
      if (this.roiRuleBatchFields.enableRoiThreshold) payload.enableRoiThreshold = this.roiRuleBatchForm.enableRoiThreshold
      if (this.roiRuleBatchFields.minCostYuan) payload.minCostYuan = this.roiRuleBatchForm.minCostYuan
      if (this.roiRuleBatchFields.waitMinutes) payload.waitMinutes = this.roiRuleBatchForm.waitMinutes
      if (this.roiRuleBatchFields.enabled) payload.enabled = !!this.roiRuleBatchForm.enabled
      if (this.roiRuleBatchFields.remark) payload.remark = this.roiRuleBatchForm.remark
      return payload
    },
    async submitRoiRuleBatchUpdate() {
      const payload = this.buildRoiRuleBatchPayload()
      if (Object.keys(payload).length <= 1) {
        this.$message.warning('请选择要修改的字段')
        return
      }
      if (payload.pauseRoiThreshold !== undefined && payload.enableRoiThreshold !== undefined &&
        Number(payload.enableRoiThreshold) < Number(payload.pauseRoiThreshold)) {
        this.$message.warning('高于开启 ROI 不能低于低于暂停 ROI')
        return
      }
      this.roiRuleBatchSaving = true
      try {
        const response = await this.$axios.post('/tencent-ai/roi-rules/batch-update', payload)
        if (response.data.success) {
          const data = response.data.data || {}
          this.$message.success('已批量修改 ' + (data.updated || 0) + ' 条规则')
          this.roiRuleBatchDialogVisible = false
          await this.refreshRoiRules()
        } else {
          this.$message.error(response.data.msg || '批量修改失败')
        }
      } catch (error) {
        this.$message.error('批量修改失败')
      } finally {
        this.roiRuleBatchSaving = false
      }
    },
    async loadRoiRuleLogs() {
      this.roiRuleLogLoading = true
      try {
        const response = await this.$axios.get('/tencent-ai/roi-rules/logs', {
          params: this.buildRoiRuleLogParams(true)
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.roiRuleLogs = data.rows || []
          this.roiRuleLogPagination.total = Number(data.total || 0)
          this.roiRuleLogPagination.page = Number(data.page || this.roiRuleLogPagination.page)
          this.roiRuleLogPagination.pageSize = Number(data.pageSize || this.roiRuleLogPagination.pageSize)
        } else {
          this.roiRuleLogs = []
          this.$message.error(response.data.msg || '日志查询失败')
        }
      } catch (error) {
        this.roiRuleLogs = []
        this.$message.error('日志查询失败')
      } finally {
        this.roiRuleLogLoading = false
      }
    },
    buildRoiRuleLogParams(withPage) {
      const params = {
        keyword: this.query.keyword
      }
      if (Array.isArray(this.roiRuleLogDateRange) && this.roiRuleLogDateRange.length === 2) {
        params.start = this.roiRuleLogDateRange[0]
        params.end = this.roiRuleLogDateRange[1]
      }
      if (withPage) {
        params.page = this.roiRuleLogPagination.page
        params.pageSize = this.roiRuleLogPagination.pageSize
      }
      return params
    },
    handleRoiRuleLogDateChange() {
      this.roiRuleLogPagination.page = 1
      this.loadRoiRuleLogs()
    },
    async exportRoiRuleLogs() {
      this.roiRuleLogExporting = true
      try {
        const response = await this.$axios.get('/tencent-ai/roi-rules/logs/export', {
          params: this.buildRoiRuleLogParams(false),
          responseType: 'blob'
        })
        this.downloadBlob(response, this.roiRuleLogExportFileName())
      } catch (error) {
        this.$message.error('日志导出失败')
      } finally {
        this.roiRuleLogExporting = false
      }
    },
    roiRuleLogExportFileName() {
      const range = Array.isArray(this.roiRuleLogDateRange) && this.roiRuleLogDateRange.length === 2
        ? this.roiRuleLogDateRange[0] + '_' + this.roiRuleLogDateRange[1]
        : this.formatDate(new Date())
      return '腾讯广告ROI自动规则日志_' + range + '.xlsx'
    },
    downloadBlob(response, fallbackName) {
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = this.downloadFileName(response, fallbackName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },
    downloadFileName(response, fallbackName) {
      const disposition = response && response.headers ? (response.headers['content-disposition'] || response.headers['Content-Disposition']) : ''
      const utf8Match = disposition && disposition.match(/filename\*=UTF-8''([^;]+)/i)
      if (utf8Match && utf8Match[1]) {
        try {
          return decodeURIComponent(utf8Match[1])
        } catch (error) {
          return utf8Match[1]
        }
      }
      return fallbackName
    },
    openRoiRuleDialog(row) {
      this.roiRuleEditing = row || null
      this.roiRuleForm = {
        businessAlias: row ? row.business_alias : '',
        pauseRoiThreshold: row ? Number(row.pause_roi_threshold || row.roi_threshold || 0) : 0.12,
        enableRoiThreshold: row ? Number(row.enable_roi_threshold || row.roi_threshold || 0) : 0.12,
        minCostYuan: row ? Number(row.min_cost_yuan || 100) : 100,
        waitMinutes: row ? Number(row.wait_minutes || 120) : 120,
        enabled: row ? Number(row.enabled) === 1 : true,
        remark: row ? (row.remark || '') : ''
      }
      this.roiRuleDialogVisible = true
      if (row && row.business_alias && this.roiRuleLabels.indexOf(row.business_alias) < 0) {
        this.roiRuleLabels.unshift(row.business_alias)
      }
      if (!this.roiRuleLabels.length) {
        this.loadRoiRuleLabels()
      }
    },
    selectedRoiRuleBusinessAliases() {
      const value = this.roiRuleForm.businessAlias
      const text = Array.isArray(value) ? value[0] : value
      return [String(text || '').trim()].filter(Boolean)
    },
    async submitRoiRule() {
      const aliases = this.selectedRoiRuleBusinessAliases()
      if (!aliases.length) {
        this.$message.warning('请选择标签')
        return
      }
      if (Number(this.roiRuleForm.enableRoiThreshold) < Number(this.roiRuleForm.pauseRoiThreshold)) {
        this.$message.warning('高于开启 ROI 不能低于低于暂停 ROI')
        return
      }
      this.roiRuleSaving = true
      const basePayload = {
        pauseRoiThreshold: this.roiRuleForm.pauseRoiThreshold,
        enableRoiThreshold: this.roiRuleForm.enableRoiThreshold,
        minCostYuan: this.roiRuleForm.minCostYuan,
        waitMinutes: this.roiRuleForm.waitMinutes,
        enabled: !!this.roiRuleForm.enabled,
        remark: this.roiRuleForm.remark
      }
      try {
        if (this.roiRuleEditing) {
          const response = await this.$axios.put('/tencent-ai/roi-rules/' + this.roiRuleEditing.id, Object.assign({}, basePayload, {
            businessAlias: aliases[0]
          }))
          if (!response.data.success) {
            this.$message.error(response.data.msg || '保存失败')
            return
          }
        } else {
          const response = await this.$axios.post('/tencent-ai/roi-rules', Object.assign({}, basePayload, {
            businessAlias: aliases[0]
          }))
          if (!response.data.success) {
            this.$message.error(response.data.msg || '保存失败')
            return
          }
        }
        this.$message.success('规则已保存')
        this.roiRuleDialogVisible = false
        await this.refreshRoiRules()
      } catch (error) {
        this.$message.error('保存失败')
      } finally {
        this.roiRuleSaving = false
      }
    },
    async toggleRoiRule(row, enabled) {
      try {
        const response = await this.$axios.post('/tencent-ai/roi-rules/' + row.id + '/toggle', { enabled })
        if (response.data.success) {
          row.enabled = enabled ? 1 : 0
          this.$message.success(enabled ? '规则已启用' : '规则已停用')
          this.loadRoiRuleLogs()
        } else {
          this.$message.error(response.data.msg || '切换失败')
          this.loadRoiRules()
        }
      } catch (error) {
        this.$message.error('切换失败')
        this.loadRoiRules()
      }
    },
    async deleteRoiRule(row) {
      try {
        await this.$confirm('确认删除该 ROI 自动规则？', '删除规则', {
          type: 'warning',
          confirmButtonText: '删除',
          cancelButtonText: '取消'
        })
      } catch (error) {
        return
      }
      try {
        const response = await this.$axios.delete('/tencent-ai/roi-rules/' + row.id)
        if (response.data.success) {
          this.$message.success('规则已删除')
          this.refreshRoiRules()
        } else {
          this.$message.error(response.data.msg || '删除失败')
        }
      } catch (error) {
        this.$message.error('删除失败')
      }
    },
    handleRoiRuleSizeChange(value) {
      this.roiRulePagination.pageSize = value
      this.roiRulePagination.page = 1
      this.loadRoiRules()
    },
    handleRoiRuleCurrentChange(value) {
      this.roiRulePagination.page = value
      this.loadRoiRules()
    },
    handleRoiRuleLogSizeChange(value) {
      this.roiRuleLogPagination.pageSize = value
      this.roiRuleLogPagination.page = 1
      this.loadRoiRuleLogs()
    },
    handleRoiRuleLogCurrentChange(value) {
      this.roiRuleLogPagination.page = value
      this.loadRoiRuleLogs()
    },
    roiRuleActionText(action) {
      const map = {
        ENABLE: '开启',
        DISABLE: '暂停',
        SKIP: '跳过',
        ERROR: '失败'
      }
      return map[String(action || '').toUpperCase()] || action || '-'
    },
    roiRuleActionType(action) {
      const upper = String(action || '').toUpperCase()
      if (upper === 'ENABLE') {
        return 'success'
      }
      if (upper === 'DISABLE') {
        return 'warning'
      }
      if (upper === 'ERROR') {
        return 'danger'
      }
      return 'info'
    },
    buildPayload() {
      return {
        start: this.dateRange && this.dateRange.length > 0 ? this.dateRange[0] : '',
        end: this.dateRange && this.dateRange.length > 1 ? this.dateRange[1] : '',
        accountId: this.query.accountId,
        adgroupId: this.query.adgroupId,
        creativeId: this.query.creativeId,
        status: this.normalizeStatusQuery(this.query.status),
        keyword: this.query.keyword,
        operationObjectType: this.query.operationObjectType,
        operationAction: this.query.operationAction,
        excludeZeroCost: !!this.query.excludeZeroCost,
        page: this.pagination.page,
        pageSize: this.pagination.pageSize,
        sortField: this.sort.field,
        sortOrder: this.sort.order
      }
    },
    applyResult(data) {
      const gameColumns = this.shouldInjectGameName()
        ? this.withGameNameColumn(data.columns || [])
        : (data.columns || [])
      const gameRows = this.shouldInjectGameName()
        ? this.withGameName(data.rows || [], data.columns || [])
        : (data.rows || [])
      this.availableColumns = this.withCurrentRevenueColumns(gameColumns)
      this.tableData = this.withCurrentRevenue(gameRows, this.availableColumns)
      this.source = data.source || ''
      this.summary = data.summary || {}
      this.pagination.total = Number(data.total || 0)
      this.pagination.page = Number(data.page || this.pagination.page)
      this.pagination.pageSize = Number(data.pageSize || this.pagination.pageSize)
      this.visibleColumns = this.defaultVisibleColumnNames(this.availableColumns)
      if (!data.exists && data.message) {
        this.$message.warning(data.message)
      }
    },
    defaultVisibleColumnNames(columns) {
      const names = (columns || []).map(column => column.name)
      if (this.activeTab === 'adgroups' && names.indexOf('bid_coefficient') >= 0 && names.indexOf('deep_bid_coefficient') >= 0) {
        return names.filter(name => name !== 'bid_adjustment_text')
      }
      return names
    },
    shouldInjectGameName() {
      return !this.isOfficialLogsTab && !this.isLogsTab
    },
    withGameName(rows, columns) {
      if (this.hasGameNameColumn(columns)) {
        return rows
      }
      return rows.map(row => {
        const item = Object.assign({}, row)
        item.game_name = this.extractGameName(row)
        return item
      })
    },
    withGameNameColumn(columns) {
      if (this.hasGameNameColumn(columns)) {
        return columns
      }
      return [{ name: 'game_name', label: '游戏名称' }].concat(columns)
    },
    hasGameNameColumn(columns) {
      return (columns || []).some(column => {
        const name = String(column.name || '').toLowerCase()
        return name === 'game_name' || name === 'gamename'
      })
    },
    extractGameName(row) {
      const candidates = [
        row.game_name,
        row.gameName,
        row.dynamic_creative_name,
        row.creative_name,
        row.material_name,
        row.adgroup_name,
        row.campaign_name
      ]
      const value = candidates.find(item => item !== null && item !== undefined && String(item).trim() !== '')
      if (!value) {
        return '-'
      }
      const text = String(value).trim()
      const splitIndex = text.search(/[-－—]/)
      if (splitIndex > 0) {
        return text.substring(0, splitIndex)
      }
      return text
    },
    withCurrentRevenue(rows, columns) {
      if (!this.canShowCurrentRevenue(columns)) {
        return rows
      }
      return rows.map(row => {
        const item = Object.assign({}, row)
        const delayHour = this.revenueDelayHour(row)
        item.current_revenue_hour = delayHour === null ? '' : 'H' + delayHour
        item.current_revenue_yuan = item.income_val_24h_yuan
        return item
      })
    },
    withCurrentRevenueColumns(columns) {
      if (!this.canShowCurrentRevenue(columns) || columns.some(column => column.name === 'current_revenue_hour')) {
        return columns
      }
      const result = columns.slice()
      const insertIndex = result.findIndex(column => column.name === 'income_val_24h_yuan')
      const derivedColumns = [
        { name: 'current_revenue_hour', label: '当前回查H' },
        { name: 'current_revenue_yuan', label: '当前回查累计回收' }
      ]
      if (insertIndex >= 0) {
        result.splice(insertIndex + 1, 0, ...derivedColumns)
      } else {
        result.push(...derivedColumns)
      }
      return result
    },
    canShowCurrentRevenue(columns) {
      const names = (columns || []).map(column => String(column.name || '').toLowerCase())
      return names.includes('stat_time_hour') && names.includes('pull_hour') && names.includes('income_val_24h_yuan')
    },
    revenueDelayHour(row) {
      const statTime = this.parseCellTime(row.stat_time_hour)
      const pullTime = this.parseCellTime(row.pull_hour)
      if (!statTime || !pullTime) {
        return null
      }
      const diff = Math.round((pullTime.getTime() - statTime.getTime()) / 3600000)
      return diff >= 0 ? diff : null
    },
    parseCellTime(value) {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const text = String(value).trim().replace(/\.0+$/, '')
      const match = text.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:Z)?$/)
      if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]), Number(match[6]))
      }
      const parsed = new Date(text)
      return Number.isNaN(parsed.getTime()) ? null : parsed
    },
    normalizeStatusQuery(status) {
      const text = String(status || '').trim()
      const map = {
        成功: 'ok',
        失败: 'failed',
        异常: 'error',
        运行中: 'running'
      }
      return map[text] || text
    },
    handleSizeChange(value) {
      this.pagination.pageSize = value
      this.pagination.page = 1
      this.loadData()
    },
    handleCurrentChange(value) {
      this.pagination.page = value
      this.loadData()
    },
    handleSortChange({ prop, order }) {
      if (!this.isCostSortColumn(prop)) {
        this.sort = { field: '', order: '' }
        return
      }
      this.sort = {
        field: order ? prop : '',
        order: order === 'ascending' ? 'asc' : (order === 'descending' ? 'desc' : '')
      }
      this.pagination.page = 1
      this.loadData()
    },
    isCostSortColumn(name) {
      const lower = String(name || '').toLowerCase()
      return lower === 'cost_yuan' ||
        lower === 'stat_cost_yuan' ||
        lower === 'backend_cost_yuan' ||
        lower === 'today_cost_yuan' ||
        lower === 'one_click_cost_yuan' ||
        lower === 'real_cost_value_explore_yuan' ||
        lower === 'cost' ||
        lower === 'stat_cost' ||
        lower === 'spend'
    },
    calculateTableHeight() {
      this.$nextTick(() => {
        const height = window.innerHeight - 390
        this.tableHeight = height < 320 ? 320 : height
      })
    },
    displaySourceName(source) {
      const key = String(source || '').trim()
      return SOURCE_LABEL_MAP[key] || key
    },
    displayColumnLabel(column) {
      const name = String((column && column.name) || '').toLowerCase()
      if (COLUMN_LABEL_MAP[name]) {
        return COLUMN_LABEL_MAP[name]
      }
      let matched = name.match(/^h(\d+)_revenue_yuan$/)
      if (matched) {
        return 'H' + matched[1] + '累计回收'
      }
      matched = name.match(/^h(\d+)_increment_yuan$/)
      if (matched) {
        return 'H' + matched[1] + '新增回收'
      }
      matched = name.match(/^roi(\d+)$/)
      if (matched) {
        return '第' + matched[1] + '日ROI'
      }
      matched = name.match(/^revenue(\d+)_yuan$/)
      if (matched) {
        return '第' + matched[1] + '日累计收入'
      }
      const backendLabel = String((column && column.label) || '').trim()
      if (backendLabel && !this.isEnglishColumnLabel(backendLabel, name)) {
        return backendLabel
      }
      return this.fallbackColumnLabel(name) || backendLabel || (column && column.name) || ''
    },
    isEnglishColumnLabel(label, name) {
      const text = String(label || '').trim()
      if (!text) {
        return true
      }
      if (/[\u4e00-\u9fa5]/.test(text)) {
        return false
      }
      const lower = text.toLowerCase()
      return lower === name || /^[a-z][a-z0-9_]*$/i.test(text) || text.indexOf('_') >= 0
    },
    fallbackColumnLabel(name) {
      const lower = String(name || '').toLowerCase()
      if (!lower) {
        return ''
      }
      if (lower.endsWith('_id')) {
        const prefix = lower.replace(/_id$/, '')
        const prefixLabel = this.fallbackColumnLabel(prefix)
        return (prefixLabel || prefix.toUpperCase()) + 'ID'
      }
      if (lower.endsWith('_json')) {
        const prefix = lower.replace(/_json$/, '')
        const prefixLabel = this.fallbackColumnLabel(prefix)
        return (prefixLabel || '配置') + '原始数据'
      }
      const parts = lower.split('_').filter(Boolean)
      if (!parts.length) {
        return ''
      }
      const labels = parts.map(part => COLUMN_PART_LABEL_MAP[part] || this.dynamicPartLabel(part))
      if (labels.every(item => !item)) {
        return ''
      }
      return labels.filter(Boolean).filter((item, index, arr) => index === 0 || item !== arr[index - 1]).join('')
    },
    dynamicPartLabel(part) {
      const roiMatch = String(part || '').match(/^roi(\d+)$/)
      if (roiMatch) {
        return '第' + roiMatch[1] + '日ROI'
      }
      const hMatch = String(part || '').match(/^h(\d+)$/)
      if (hMatch) {
        return 'H' + hMatch[1]
      }
      return ''
    },
    columnWidth(name) {
      const lower = String(name || '').toLowerCase()
      if (lower.includes('targeting') || lower.includes('site_set') || lower.includes('scene_spec')) {
        return 260
      }
      if (lower.includes('name') || lower.includes('message') || lower.includes('error')) {
        return 220
      }
      if (lower === 'game_name' || lower === 'gamename' || lower.includes('game_name')) {
        return 170
      }
      if (lower.includes('time') || lower.includes('date') || lower.includes('hour')) {
        return 160
      }
      if (lower.includes('creative') || lower.includes('material') || lower.includes('account') || lower.includes('advertiser')) {
        return 170
      }
      return 120
    },
    valueClass(name, value) {
      const lower = String(name || '').toLowerCase()
      if (lower.includes('cost') || lower.includes('spend')) {
        return 'value-cost'
      }
      if (lower.includes('recovery') || lower.includes('income') || lower.includes('revenue') || lower.includes('pay')) {
        return 'value-recovery'
      }
      if (lower.indexOf('roi') >= 0) {
        const numberValue = Number(value)
        if (Number.isNaN(numberValue)) {
          return 'value-roi'
        }
        if (numberValue >= 1) {
          return 'value-good'
        }
        if (numberValue > 0) {
          return 'value-warning'
        }
        return 'value-muted'
      }
      return ''
    },
    formatCell(value, columnName) {
      const lowerColumnName = String(columnName || '').toLowerCase()
      if (lowerColumnName === 'bid_adjustment_text' && (value === null || value === undefined || value === '')) {
        return '\u65e0\u8c03\u6574'
      }
      if (lowerColumnName === 'auto_acquisition_status_text' && (value === null || value === undefined || value === '')) {
        return '\u65e0\u8865\u5145\u8bf4\u660e'
      }
      if (lowerColumnName === 'priority_site_set_text' && (value === null || value === undefined || value === '')) {
        return '\u672a\u8bbe\u7f6e'
      }
      if (this.isBusinessDefaultColumn(lowerColumnName) && (value === null || value === undefined || value === '')) {
        return this.businessDefaultText(lowerColumnName)
      }
      if (this.isUnlimitedTargetingColumn(columnName) && (value === null || value === undefined || value === '')) {
        return '\u4e0d\u9650'
      }
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      if (lowerColumnName === 'automatic_site_enabled') {
        return Number(value) ? '\u662f' : '\u5426'
      }
      if (lowerColumnName === 'bid_adjustment_text') {
        return this.formatBidAdjustment(value)
      }
      if (lowerColumnName === 'time_series') {
        return this.formatTimeSeries(value)
      }
      const temporalValue = this.formatTemporalCell(value, columnName)
      if (temporalValue !== null) {
        return temporalValue
      }
      const translatedValue = this.translateCellValue(value, columnName)
      if (translatedValue !== null) {
        return translatedValue
      }
      if (this.isIdColumn(columnName)) {
        return String(value)
      }
      if (this.isPercentColumn(columnName)) {
        return this.formatPercent(value)
      }
      if (typeof value === 'number') {
        return this.formatNumber(value)
      }
      if (Array.isArray(value)) {
        return value.map(item => typeof item === 'object' ? JSON.stringify(item) : String(item)).join('；')
      }
      if (typeof value === 'object') {
        return JSON.stringify(value)
      }
      const text = String(value)
      if (/^-?\d+(\.\d+)?$/.test(text) && text.length < 18) {
        return this.formatNumber(Number(text))
      }
      return text
    },
    formatTemporalCell(value, columnName) {
      const lower = String(columnName || '').toLowerCase()
      if (!lower.includes('time') && !lower.includes('date') && !lower.includes('hour') && lower !== 'ds') {
        return null
      }
      const text = String(value).trim()
      const dateTimeMatch = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.\d+)?(?:Z)?$/)
      if (dateTimeMatch) {
        if (lower.includes('date') && !lower.includes('time') && !lower.includes('hour')) {
          return dateTimeMatch[1]
        }
        return dateTimeMatch[1] + ' ' + dateTimeMatch[2]
      }
      const isoDateMatch = text.match(/^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.\d+)?Z?$/)
      if (isoDateMatch && lower.includes('date') && !lower.includes('time') && !lower.includes('hour')) {
        return isoDateMatch[1]
      }
      return text.replace(/(\d{2}:\d{2}:\d{2})\.0+\b/, '$1')
    },
    isUnlimitedTargetingColumn(name) {
      const lower = String(name || '').toLowerCase()
      return lower === 'targeting_age_text' ||
        lower === 'targeting_gender_text' ||
        lower === 'targeting_geo_text' ||
        lower === 'scene_spec_text'
    },
    isBusinessDefaultColumn(name) {
      const lower = String(name || '').toLowerCase()
      return lower === 'smart_delivery_platform' ||
        lower === 'smart_delivery_scene' ||
        lower === 'smart_bid_type'
    },
    businessDefaultText(name) {
      const lower = String(name || '').toLowerCase()
      if (lower === 'smart_delivery_platform') {
        return '\u975e\u667a\u6295'
      }
      if (lower === 'smart_delivery_scene') {
        return '\u666e\u901a\u6295\u653e'
      }
      if (lower === 'smart_bid_type') {
        return '\u4e0d\u9002\u7528'
      }
      return '-'
    },
    isPercentColumn(name) {
      const lower = String(name || '').toLowerCase()
      return lower === 'ctr' ||
        lower.endsWith('_rate') ||
        lower.includes('retention') ||
        lower.includes('click_activated_rate') ||
        lower.includes('register_rate') ||
        lower.includes('conversions_rate')
    },
    formatPercent(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const numberValue = Number(value)
      if (Number.isNaN(numberValue)) {
        return String(value)
      }
      const percentValue = Math.abs(numberValue) <= 1 ? numberValue * 100 : numberValue
      return percentValue.toLocaleString('zh-CN', { maximumFractionDigits: 2 }) + '%'
    },
    translateCellValue(value, columnName) {
      const lower = String(columnName || '').toLowerCase()
      if (this.isUnlimitedTargetingColumn(columnName) && this.isEmptyConfigText(value)) {
        return '\u4e0d\u9650'
      }
      if (lower === 'search_expansion_switch' || lower === 'ecom_pkam_switch') {
        const enumText = String(value || '').trim().toUpperCase()
        if (enumText.endsWith('_OPEN')) {
          return '\u6253\u5f00'
        }
        if (enumText.endsWith('_CLOSE')) {
          return '\u5173\u95ed'
        }
      }
      if (lower === 'raw_json' || lower === 'extra_json' || lower.endsWith('_json')) {
        return '已记录'
      }
      if (lower === 'auto_acquisition_enabled' || lower === 'automatic_site_enabled' || lower === 'flow_optimization_enabled' || lower === 'aoi_optimization_strategy_enabled' || lower === 'field_present' || lower === 'revenue_present') {
        const text = String(value)
        if (text === '1' || text.toLowerCase() === 'true') {
          return '是'
        }
        if (text === '0' || text.toLowerCase() === 'false') {
          return '否'
        }
      }
      const text = String(value).trim()
      const translated = this.translateTextValue(text)
      if (translated !== text) {
        return translated
      }
      return null
    },
    translateTextValue(text) {
      const raw = String(text || '').trim()
      if (!raw) {
        return raw
      }
      if (VALUE_LABEL_MAP[raw]) {
        return VALUE_LABEL_MAP[raw]
      }
      const upper = raw.toUpperCase()
      if (VALUE_LABEL_MAP[upper]) {
        return VALUE_LABEL_MAP[upper]
      }
      if (/^[A-Z][A-Z0-9_]+$/.test(raw)) {
        return this.fallbackColumnLabel(raw.toLowerCase()) || raw
      }
      return raw
    },
    isEmptyConfigText(value) {
      const text = String(value === null || value === undefined ? '' : value).trim()
      return text === '' || text === '{}' || text === '[]' || text.toLowerCase() === 'null'
    },
    formatBidAdjustment(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const text = String(value).trim()
      if (this.isEmptyConfigText(text)) {
        return '无调整'
      }
      try {
        const data = JSON.parse(text)
        const packages = data && data.site_set_package
        if (Array.isArray(packages) && packages.length) {
          return packages.map(item => {
            const sites = this.formatLabelList(item.site_set) || '版位'
            const parts = []
            if (item.bid_coefficient !== null && item.bid_coefficient !== undefined) {
              parts.push('浅层提价系数 ' + this.formatNumber(item.bid_coefficient))
            }
            if (item.deep_bid_coefficient !== null && item.deep_bid_coefficient !== undefined) {
              parts.push('深层提价系数 ' + this.formatNumber(item.deep_bid_coefficient))
            }
            return sites + (parts.length ? '：' + parts.join('，') : '')
          }).join('；')
        }
        return this.formatJsonObject(data)
      } catch (error) {
        return this.translateTextValue(text)
      }
    },
    formatLabelList(value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      const list = Array.isArray(value) ? value : [value]
      return list.map(item => this.formatJsonValue(item)).filter(Boolean).join('、')
    },
    formatJsonObject(value) {
      if (value === null || value === undefined) {
        return '-'
      }
      if (Array.isArray(value)) {
        return value.map(item => this.formatJsonValue(item)).filter(Boolean).join('、')
      }
      if (typeof value === 'object') {
        const entries = Object.keys(value).map(key => {
          const label = JSON_KEY_LABEL_MAP[key] || this.fallbackColumnLabel(key) || key
          return label + '：' + this.formatJsonValue(value[key])
        })
        return entries.filter(Boolean).join('；')
      }
      return this.formatJsonValue(value)
    },
    formatJsonValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      if (Array.isArray(value)) {
        return value.map(item => this.formatJsonValue(item)).filter(Boolean).join('、')
      }
      if (typeof value === 'object') {
        return this.formatJsonObject(value)
      }
      if (typeof value === 'number') {
        return this.formatNumber(value)
      }
      return this.translateTextValue(String(value))
    },
    formatTimeSeries(value) {
      const text = String(value)
      if (!/^[01]{336}$/.test(text)) {
        return text
      }
      const enabledCount = (text.match(/1/g) || []).length
      if (enabledCount === 336) {
        return '全天投放'
      }
      if (enabledCount === 0) {
        return '未设置投放时段'
      }
      return '分时段投放（' + enabledCount + '/336）'
    },
    isIdColumn(name) {
      const lower = String(name || '').toLowerCase()
      return lower === 'id' ||
        lower.endsWith('_id') ||
        lower.includes('id_') ||
        lower.includes('_id_') ||
        lower === 'aadvid' ||
        lower === 'appid' ||
        lower === 'outid' ||
        lower === 'out_id'
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const numberValue = Number(value)
      if (Number.isNaN(numberValue)) {
        return String(value)
      }
      return numberValue.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    formatDecimal(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const numberValue = Number(value)
      if (Number.isNaN(numberValue)) {
        return String(value)
      }
      return numberValue.toLocaleString('zh-CN', { maximumFractionDigits: 4 })
    },
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return year + '-' + month + '-' + day
    },
    exportCurrentPage() {
      if (!this.tableData.length || !this.renderedColumns.length) {
        this.$message.warning('当前页没有可导出的数据')
        return
      }
      const lines = []
      lines.push(this.renderedColumns.map(column => this.csvCell(this.displayColumnLabel(column))).join(','))
      this.tableData.forEach(row => {
        lines.push(this.renderedColumns.map(column => this.csvCell(this.formatCell(row[column.name], column.name))).join(','))
      })
      const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '腾讯广告AI数据_' + this.activeConfig.label + '_' + this.formatDate(new Date()) + '.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },
    csvCell(value) {
      return '"' + String(value === null || value === undefined ? '' : value).replace(/"/g, '""') + '"'
    }
  }
}
</script>

<style scoped>
.tencent-ai-data-page {
  min-height: 100%;
  padding: 12px 14px 18px;
  background: #f5f7fb;
  color: #303133;
  box-sizing: border-box;
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.page-head h2 {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
}

.page-head span {
  color: #8c939d;
  font-size: 12px;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.query-panel,
.table-panel,
.status-panel,
.roi-rule-panel {
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.query-panel {
  padding: 10px 12px 4px;
}

.query-form {
  margin-top: 8px;
}

.roi-rule-query {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(140px, 1fr));
  gap: 10px;
  margin: 10px 0;
}

.summary-card {
  min-height: 72px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
  box-sizing: border-box;
}

.summary-card span {
  display: block;
  color: #8c939d;
  font-size: 12px;
  line-height: 18px;
}

.summary-card strong {
  display: block;
  margin-top: 8px;
  color: #303133;
  font-size: 20px;
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.summary-card strong.cost,
.value-cost {
  color: #409eff;
}

.summary-card strong.recovery,
.value-recovery,
.value-good {
  color: #67c23a;
}

.summary-card strong.roi,
.value-roi,
.value-warning {
  color: #e6a23c;
}

.value-muted {
  color: #909399;
}

.table-panel {
  padding: 8px;
}

.roi-rule-panel {
  margin-top: 10px;
  padding: 8px;
}

.roi-auth-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
  color: #606266;
  font-size: 12px;
}

.roi-auth-main,
.roi-auth-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.roi-auth-title {
  color: #303133;
  font-size: 13px;
  font-weight: 600;
}

.roi-log-toolbar {
  margin-top: 16px;
}

.danger-link {
  color: #f56c6c;
}

.form-hint {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.label-quick-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 4px;
  line-height: 18px;
}

.selected-label-count {
  color: #909399;
  font-size: 12px;
}

.selected-count {
  margin-bottom: 12px;
  color: #606266;
  font-size: 13px;
}

.status-switch-cell {
  min-width: 128px;
  white-space: nowrap;
}

.batch-field-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.batch-field-row.align-start {
  align-items: flex-start;
}

.batch-field-row .el-input-number,
.batch-field-row .el-textarea {
  width: 100%;
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  color: #606266;
  font-size: 12px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.column-options {
  max-height: 320px;
  overflow-y: auto;
}

.column-options /deep/ .el-checkbox {
  display: block;
  margin: 0 0 8px 0;
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  color: #606266;
  font-size: 12px;
}

.status-panel {
  margin-top: 10px;
  padding: 10px;
}

.status-title {
  margin-bottom: 8px;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

::v-deep .el-tabs__header {
  margin-bottom: 0;
}

::v-deep .el-table {
  font-size: 12px;
}

::v-deep .el-table .cell {
  white-space: nowrap;
}

@media (max-width: 1200px) {
  .summary-grid {
    grid-template-columns: repeat(3, minmax(140px, 1fr));
  }
}

@media (max-width: 760px) {
  .page-head,
  .roi-auth-panel,
  .pagination-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .roi-auth-main,
  .roi-auth-actions {
    align-items: flex-start;
    flex-direction: column;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
